import { useState, useEffect } from 'react';
import { useNavigate, useParams, useOutletContext } from 'react-router-dom';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { OnboardingProject } from '@/types/onboarding';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import {
  CheckCircle2,
  Rocket,
  Calendar,
  Users,
  Database,
  Settings,
  AlertCircle,
  XCircle,
  Clock,
  Loader2,
  Building2,
  FileText,
  Mail,
  Shield,
  PartyPopper,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { 
  usePreFlightChecklist, 
  useActivateProject, 
  useHypercareStatus,
  PreFlightItem,
  ActivationStep
} from '../hooks/useGoLive';

interface OutletContext {
  project: OnboardingProject | null;
  setProject: (project: OnboardingProject | null) => void;
  loadProject: () => Promise<void>;
}

const ACTIVATION_STEPS: ActivationStep[] = [
  { id: 'verify', name: 'Verify Pre-Flight', status: 'pending' },
  { id: 'entities', name: 'Create TUM Entities', status: 'pending' },
  { id: 'sources', name: 'Activate Bill Sources', status: 'pending' },
  { id: 'notify', name: 'Send Notifications', status: 'pending' },
  { id: 'hypercare', name: 'Start Hypercare', status: 'pending' },
];

const Stage7GoLive = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const { currentWorkspace } = useWorkspace();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { project, setProject, loadProject } = useOutletContext<OutletContext>();

  const [goLiveDate, setGoLiveDate] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [isActivating, setIsActivating] = useState(false);
  const [activationSteps, setActivationSteps] = useState<ActivationStep[]>(ACTIVATION_STEPS);
  const [currentStepIndex, setCurrentStepIndex] = useState(-1);

  // Hooks
  const { data: preFlightItems, isLoading: isLoadingChecklist, refetch: refetchChecklist } = usePreFlightChecklist(projectId);
  const { data: hypercareStatus } = useHypercareStatus(projectId);
  const activateMutation = useActivateProject();

  const isLive = project?.overall_status === 'live';
  const allChecksPassed = preFlightItems?.every(item => !item.required || item.status === 'passed');

  useEffect(() => {
    if (project?.target_go_live_date) {
      setGoLiveDate(project.target_go_live_date.split('T')[0]);
    }
  }, [project]);

  const simulateActivationProgress = async () => {
    for (let i = 0; i < ACTIVATION_STEPS.length; i++) {
      setCurrentStepIndex(i);
      setActivationSteps(prev => prev.map((step, idx) => ({
        ...step,
        status: idx < i ? 'completed' : idx === i ? 'in_progress' : 'pending'
      })));
      await new Promise(resolve => setTimeout(resolve, 800));
    }
    setActivationSteps(prev => prev.map(step => ({ ...step, status: 'completed' })));
  };

  const handleActivate = async () => {
    if (!project || !goLiveDate || !currentWorkspace?.id) {
      toast({
        title: 'Validation Error',
        description: 'Please select a go-live date',
        variant: 'destructive',
      });
      return;
    }

    if (!allChecksPassed) {
      toast({
        title: 'Pre-Flight Check Failed',
        description: 'Please ensure all required checks pass before activating',
        variant: 'destructive',
      });
      return;
    }

    setIsActivating(true);
    
    // Start progress simulation
    simulateActivationProgress();

    try {
      await activateMutation.mutateAsync({
        projectId: project.id,
        workspaceId: currentWorkspace.id,
        goLiveDate: new Date(goLiveDate).toISOString(),
        notes: notes || undefined,
      });

      await loadProject();
    } catch (error) {
      setActivationSteps(ACTIVATION_STEPS);
      setCurrentStepIndex(-1);
    } finally {
      setIsActivating(false);
    }
  };

  const getStatusIcon = (status: PreFlightItem['status']) => {
    switch (status) {
      case 'passed':
        return <CheckCircle2 className="h-5 w-5 text-green-600" />;
      case 'failed':
        return <XCircle className="h-5 w-5 text-destructive" />;
      case 'checking':
        return <Loader2 className="h-5 w-5 text-muted-foreground animate-spin" />;
      default:
        return <Clock className="h-5 w-5 text-amber-500" />;
    }
  };

  const getStepIcon = (step: ActivationStep) => {
    const icons: Record<string, typeof Database> = {
      verify: Shield,
      entities: Database,
      sources: Mail,
      notify: FileText,
      hypercare: Users,
    };
    const Icon = icons[step.id] || Settings;

    if (step.status === 'completed') {
      return <CheckCircle2 className="h-5 w-5 text-green-600" />;
    }
    if (step.status === 'in_progress') {
      return <Loader2 className="h-5 w-5 text-primary animate-spin" />;
    }
    return <Icon className="h-5 w-5 text-muted-foreground" />;
  };

  // Loading state
  if (!project) {
    return (
      <div className="max-w-6xl mx-auto space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-4 w-96" />
        <div className="grid gap-6">
          <Skeleton className="h-64" />
          <div className="grid grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-24" />)}
          </div>
          <Skeleton className="h-48" />
        </div>
      </div>
    );
  }

  // Live state with hypercare
  if (isLive) {
    return (
      <div className="max-w-6xl mx-auto space-y-6">
        <Card className="border-green-200 bg-green-50 dark:bg-green-950/20 dark:border-green-800">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center text-center space-y-4 py-8">
              <div className="h-20 w-20 rounded-full bg-green-600 flex items-center justify-center">
                <PartyPopper className="h-10 w-10 text-white" />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-green-900 dark:text-green-100">Project is Live!</h2>
                <p className="text-green-700 dark:text-green-300 mt-2">
                  {project.customer_name} has been successfully activated
                </p>
                {project.actual_go_live_date && (
                  <p className="text-sm text-green-600 dark:text-green-400 mt-1">
                    Go-Live Date: {new Date(project.actual_go_live_date).toLocaleDateString()}
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Hypercare Status */}
        {hypercareStatus?.is_active && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-primary" />
                Hypercare Period
              </CardTitle>
              <CardDescription>
                Enhanced monitoring and support for the first 7 days
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Progress</span>
                <span className="text-sm font-medium">{hypercareStatus.days_remaining} days remaining</span>
              </div>
              <Progress value={hypercareStatus.progress_percentage} className="h-3" />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Started: {new Date(hypercareStatus.start_date).toLocaleDateString()}</span>
                <span>Ends: {new Date(hypercareStatus.end_date).toLocaleDateString()}</span>
              </div>
              <div className="p-4 bg-muted/50 rounded-lg mt-4">
                <h4 className="font-medium mb-2">Hypercare Includes:</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Priority support response (under 2 hours)</li>
                  <li>• Daily bill processing verification</li>
                  <li>• Automated anomaly detection alerts</li>
                  <li>• Direct access to onboarding specialist</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Activation Summary */}
        <Card>
          <CardHeader>
            <CardTitle>Activation Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-4 bg-muted/50 rounded-lg text-center">
                <Building2 className="h-6 w-6 mx-auto text-muted-foreground mb-2" />
                <p className="text-2xl font-bold">{project.estimated_properties || 0}</p>
                <p className="text-sm text-muted-foreground">Properties</p>
              </div>
              <div className="p-4 bg-muted/50 rounded-lg text-center">
                <Database className="h-6 w-6 mx-auto text-muted-foreground mb-2" />
                <p className="text-2xl font-bold">{project.estimated_units || 0}</p>
                <p className="text-sm text-muted-foreground">Units</p>
              </div>
              <div className="p-4 bg-muted/50 rounded-lg text-center">
                <FileText className="h-6 w-6 mx-auto text-muted-foreground mb-2" />
                <p className="text-2xl font-bold">{project.estimated_accounts || 0}</p>
                <p className="text-sm text-muted-foreground">Accounts</p>
              </div>
              <div className="p-4 bg-muted/50 rounded-lg text-center">
                <CheckCircle2 className="h-6 w-6 mx-auto text-green-600 mb-2" />
                <p className="text-2xl font-bold">100%</p>
                <p className="text-sm text-muted-foreground">Complete</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex gap-4">
          <Button onClick={() => navigate(`/w/${currentWorkspace?.id}/dashboard/tum/onboarding`)}>
            View All Projects
          </Button>
          <Button variant="outline" onClick={() => navigate(`/w/${currentWorkspace?.id}/dashboard/tum`)}>
            Go to TUM Dashboard
          </Button>
        </div>
      </div>
    );
  }

  // Activation in progress
  if (isActivating) {
    const progressPercent = ((currentStepIndex + 1) / ACTIVATION_STEPS.length) * 100;

    return (
      <div className="max-w-6xl mx-auto space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Rocket className="h-5 w-5 text-primary animate-pulse" />
              Activating Project...
            </CardTitle>
            <CardDescription>
              Please wait while we set up your TUM environment
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <Progress value={progressPercent} className="h-3" />
            
            <div className="space-y-3">
              {activationSteps.map((step, idx) => (
                <div 
                  key={step.id} 
                  className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${
                    step.status === 'in_progress' ? 'bg-primary/10' : 
                    step.status === 'completed' ? 'bg-green-50 dark:bg-green-950/20' : 
                    'bg-muted/50'
                  }`}
                >
                  {getStepIcon(step)}
                  <span className={step.status === 'completed' ? 'text-green-700 dark:text-green-300' : ''}>
                    {step.name}
                  </span>
                  {step.status === 'in_progress' && (
                    <Badge variant="outline" className="ml-auto">In Progress</Badge>
                  )}
                  {step.status === 'completed' && (
                    <Badge className="ml-auto bg-green-600">Complete</Badge>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div>
        <h2 className="text-2xl font-semibold">Stage 7: Go-Live</h2>
        <p className="text-muted-foreground mt-1">
          Finalize and activate the onboarding project
        </p>
      </div>

      {/* Pre-Go-Live Checklist */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Pre-Go-Live Checklist</CardTitle>
              <CardDescription>
                All required items must pass before activation
              </CardDescription>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => refetchChecklist()}
              disabled={isLoadingChecklist}
            >
              {isLoadingChecklist ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                'Refresh'
              )}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {isLoadingChecklist ? (
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map(i => (
                <div key={i} className="flex items-center gap-3">
                  <Skeleton className="h-5 w-5 rounded-full" />
                  <Skeleton className="h-4 w-64" />
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-3">
              {preFlightItems?.map((item) => (
                <div 
                  key={item.id} 
                  className={`flex items-start gap-3 p-3 rounded-lg ${
                    item.status === 'passed' ? 'bg-green-50 dark:bg-green-950/20' :
                    item.status === 'failed' ? 'bg-red-50 dark:bg-red-950/20' :
                    'bg-amber-50 dark:bg-amber-950/20'
                  }`}
                >
                  {getStatusIcon(item.status)}
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className={`font-medium ${
                        item.status === 'passed' ? 'text-green-900 dark:text-green-100' :
                        item.status === 'failed' ? 'text-red-900 dark:text-red-100' :
                        'text-amber-900 dark:text-amber-100'
                      }`}>
                        {item.label}
                      </span>
                      {item.required && (
                        <Badge variant="outline" className="text-xs">Required</Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mt-0.5">
                      {item.description}
                    </p>
                    {item.details && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {item.details}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Project Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Building2 className="h-8 w-8 text-muted-foreground" />
              <div>
                <p className="text-sm text-muted-foreground">Properties</p>
                <p className="text-2xl font-bold">{project.estimated_properties || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Users className="h-8 w-8 text-muted-foreground" />
              <div>
                <p className="text-sm text-muted-foreground">Units</p>
                <p className="text-2xl font-bold">{project.estimated_units || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Settings className="h-8 w-8 text-muted-foreground" />
              <div>
                <p className="text-sm text-muted-foreground">Accounts</p>
                <p className="text-2xl font-bold">{project.estimated_accounts || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Calendar className="h-8 w-8 text-muted-foreground" />
              <div>
                <p className="text-sm text-muted-foreground">Target Date</p>
                <p className="text-lg font-bold">
                  {project.target_go_live_date
                    ? new Date(project.target_go_live_date).toLocaleDateString()
                    : 'Not set'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Go-Live Form */}
      <Card>
        <CardHeader>
          <CardTitle>Go-Live Configuration</CardTitle>
          <CardDescription>
            Set the go-live date and add any final notes
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <Label htmlFor="go-live-date">Go-Live Date *</Label>
            <Input
              id="go-live-date"
              type="date"
              value={goLiveDate}
              onChange={(e) => setGoLiveDate(e.target.value)}
              min={new Date().toISOString().split('T')[0]}
              className="mt-2"
            />
            <p className="text-sm text-muted-foreground mt-1">
              Select the date when the project will go live
            </p>
          </div>

          <div>
            <Label htmlFor="notes">Notes (Optional)</Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any final notes or instructions for the go-live..."
              rows={4}
              className="mt-2"
            />
          </div>

          {!allChecksPassed && (
            <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-destructive mt-0.5" />
                <div>
                  <p className="font-medium text-destructive">Cannot Activate</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Please resolve all required pre-flight checks before activating the project.
                  </p>
                </div>
              </div>
            </div>
          )}

          {allChecksPassed && (
            <div className="p-4 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-md">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-900 dark:text-amber-100">Ready to Activate</p>
                  <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                    All checks passed. Once activated, bill sources will begin processing and 
                    a 7-day hypercare period will start with enhanced monitoring.
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="flex gap-4 pt-4">
            <Button
              onClick={handleActivate}
              disabled={!goLiveDate || !allChecksPassed || isActivating}
              className="flex-1"
              size="lg"
            >
              <Rocket className="h-4 w-4 mr-2" />
              Activate Project
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate(`/w/${currentWorkspace?.id}/dashboard/tum/onboarding/project/${projectId}/stage-6`)}
              size="lg"
            >
              ← Back to Sign-Off
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* What Happens Next */}
      <Card>
        <CardHeader>
          <CardTitle>What Happens After Activation?</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="flex items-start gap-3 p-4 bg-muted/50 rounded-lg">
              <Database className="h-5 w-5 text-primary mt-0.5" />
              <div>
                <p className="font-medium">TUM Entities Created</p>
                <p className="text-sm text-muted-foreground">
                  Properties, units, accounts, and tenants will be created in the production system
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-4 bg-muted/50 rounded-lg">
              <Mail className="h-5 w-5 text-primary mt-0.5" />
              <div>
                <p className="font-medium">Bill Collection Starts</p>
                <p className="text-sm text-muted-foreground">
                  Configured bill sources will begin checking for new bills automatically
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-4 bg-muted/50 rounded-lg">
              <Settings className="h-5 w-5 text-primary mt-0.5" />
              <div>
                <p className="font-medium">Configurations Applied</p>
                <p className="text-sm text-muted-foreground">
                  GL code mappings, tags, and workflow rules become active
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-4 bg-muted/50 rounded-lg">
              <Shield className="h-5 w-5 text-primary mt-0.5" />
              <div>
                <p className="font-medium">7-Day Hypercare</p>
                <p className="text-sm text-muted-foreground">
                  Enhanced monitoring, priority support, and daily verification checks
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Stage7GoLive;
